import { useEffect, useState, useMemo } from "react";
import { api, extractError, API_BASE, getToken } from "@/lib/api";
import { useBrands } from "@/contexts/BrandContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus, Pencil, Star, Upload as UploadIcon, FileSpreadsheet, PowerOff, Power,
  Loader2,
} from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { useCategories } from "@/hooks/useCategories";
import { InfoTip } from "@/components/app/InfoTip";

const INV_OPTIONS = ["Healthy", "Low", "Out of Stock", "Hold"];
const LIFECYCLE = ["Launch", "Scale", "Mature", "Liquidation", "Hold"];

const EMPTY = {
  product_name: "", sku: "", asin: "", marketplace: "Amazon",
  category: "", subcategory: "", product_type: "",
  features: "", ingredients: "", benefits: "", target_customer: "",
  price: "", mrp: "", margin_percentage: "",
  inventory_status: "Healthy", lifecycle_stage: "Scale",
  is_hero_sku: false,
  amazon_listing_url: "", current_title: "", current_bullets: "", current_backend_keywords: "",
  claim_restrictions: "",
};

export default function Products() {
  const { selectedBrandId, selectedBrand } = useBrands();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ active: 0, inactive: 0, total: 0 });
  const [tab, setTab] = useState("active"); // "active" | "inactive"
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const { categories } = useCategories();
  // Phase 19.4 — client-side filters on the products list (search + price band).
  const [filterText, setFilterText] = useState("");
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");

  // Subcategories filter to selected category
  const selectedCat = useMemo(
    () => categories.find((c) => c.category_name === form.category),
    [categories, form.category],
  );
  const subOptions = selectedCat?.subcategories || [];

  // Filtered list driving the table — search across name / SKU / ASIN, plus
  // an inclusive price band (₹). Empty bounds short-circuit so the user can
  // set just one side.
  const visibleItems = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    const lo = priceMin === "" ? null : Number(priceMin);
    const hi = priceMax === "" ? null : Number(priceMax);
    return (items || []).filter((p) => {
      if (q) {
        const hay = `${p.product_name || ""} ${p.sku || ""} ${p.asin || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      const price = p.price == null ? null : Number(p.price);
      if (lo != null && (price == null || price < lo)) return false;
      if (hi != null && (price == null || price > hi)) return false;
      return true;
    });
  }, [items, filterText, priceMin, priceMax]);

  const load = () => {
    if (!selectedBrandId) { setItems([]); setCounts({ active: 0, inactive: 0, total: 0 }); return; }
    api.get(`/products?brand_id=${selectedBrandId}&status=${tab}`).then((r) => setItems(r.data || []));
    api.get(`/products/counts?brand_id=${selectedBrandId}`).then((r) => setCounts(r.data || { active: 0, inactive: 0, total: 0 })).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [selectedBrandId, tab]);

  const save = async () => {
    if (!selectedBrandId) return toast.error("Select a brand first");
    try {
      const payload = {
        ...form,
        brand_id: selectedBrandId,
        price: form.price ? Number(form.price) : null,
        mrp: form.mrp ? Number(form.mrp) : null,
        margin_percentage: form.margin_percentage ? Number(form.margin_percentage) : null,
      };
      if (editing) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success(editing ? "Product updated — rematching uploads in background" : "Product created — rematching uploads in background");
      setOpen(false); setEditing(null); setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  const onEdit = (p) => { setEditing(p); setForm({ ...EMPTY, ...p }); setOpen(true); };

  const setStatus = async (p, status) => {
    try {
      await api.patch(`/products/${p.id}/status`, { status });
      toast.success(`${p.product_name} ${status === "active" ? "reactivated" : "deactivated"} — rematching in background`);
      load();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  // ---- Bulk upload state ----
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkMarketplace, setBulkMarketplace] = useState("Amazon");
  const [bulkPreview, setBulkPreview] = useState(null);   // dry-run output
  const [bulkResult, setBulkResult] = useState(null);     // committed import output
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkPreviewing, setBulkPreviewing] = useState(false);
  const [bulkTemplate, setBulkTemplate] = useState(null);

  useEffect(() => {
    if (!bulkOpen) return;
    const mp = selectedBrand?.marketplace || "Amazon";
    setBulkMarketplace(mp);
    setBulkPreview(null); setBulkResult(null);
  }, [bulkOpen, selectedBrand]);

  useEffect(() => {
    if (!bulkOpen) return;
    api.get(`/products/bulk-upload/template?marketplace=${encodeURIComponent(bulkMarketplace)}`)
      .then((r) => setBulkTemplate(r.data))
      .catch(() => setBulkTemplate(null));
  }, [bulkOpen, bulkMarketplace]);

  const [bulkProgress, setBulkProgress] = useState(0);
  const [bulkServerWait, setBulkServerWait] = useState(false);

  const _send = async (dryRun) => {
    const fd = new FormData();
    fd.append("brand_id", selectedBrandId);
    fd.append("marketplace", bulkMarketplace);
    fd.append("dry_run", dryRun ? "true" : "false");
    fd.append("file", bulkFile);
    setBulkProgress(0); setBulkServerWait(false);
    const res = await api.post("/products/bulk-upload", fd, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (e) => {
        if (!e.total) return;
        const pct = Math.min(100, Math.round((e.loaded / e.total) * 100));
        setBulkProgress(pct);
        if (pct >= 100) setBulkServerWait(true);
      },
    });
    setBulkServerWait(false);
    return res.data;
  };

  const previewBulk = async () => {
    if (!selectedBrandId) return toast.error("Select a brand first");
    if (!bulkFile) return toast.error("Pick a CSV/XLSX file");
    setBulkPreviewing(true);
    try {
      const data = await _send(true);
      setBulkPreview(data); setBulkResult(null);
      toast.success(`Preview ready: ${data.would_create} new · ${data.would_update} updates · ${data.skipped} skipped`);
    } catch (e) {
      toast.error(e.message || "Preview failed");
    } finally { setBulkPreviewing(false); }
  };

  const commitBulk = async () => {
    if (!bulkPreview) return toast.error("Run preview first");
    setBulkSubmitting(true);
    try {
      const data = await _send(false);
      setBulkResult(data); setBulkPreview(null);
      toast.success(`Imported: +${data.created} new · ↻${data.updated} updated · ⤬${data.skipped} skipped — rematching uploads`);
      load();
    } catch (e) {
      toast.error(e.message || "Import failed");
    } finally { setBulkSubmitting(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Masters</p>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Products</h1>
          <p className="mt-1 text-sm text-slate-500">Manage SKUs, ingredients, lifecycle and inventory state. Inactive SKUs are excluded from search-term matching.</p>
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={bulkOpen} onOpenChange={(v) => { setBulkOpen(v); if (!v) { setBulkFile(null); setBulkPreview(null); setBulkResult(null); } }}>
            <DialogTrigger asChild>
              <Button data-testid="product-bulk-upload-btn" disabled={!selectedBrandId} variant="outline" className="rounded-lg gap-1.5">
                <UploadIcon className="h-4 w-4" /> Bulk import
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Bulk import products</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 text-xs text-slate-700 space-y-1.5">
                  <div className="flex items-center gap-2 text-slate-900 font-semibold">
                    <FileSpreadsheet className="h-3.5 w-3.5" /> File requirements
                  </div>
                  <p>Upload a CSV or XLSX with these columns. We upsert on <span className="font-mono font-semibold">{bulkTemplate?.marketplace_id_label || "marketplace ID"}</span> — re-running with the same file is safe.</p>
                  <p className="text-slate-600"><span className="font-semibold">Required:</span> {(bulkTemplate?.required_headers || []).join(", ") || "—"}</p>
                  <p className="text-slate-500"><span className="font-semibold">Recommended:</span> {(bulkTemplate?.recommended_headers || []).join(", ") || "—"}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block inline-flex items-center gap-1">
                      Marketplace <InfoTip term="ASIN" title="Marketplace identifier" text="Each marketplace has its own unique product ID (Amazon=ASIN, Flipkart=FSN, Nykaa=Nykaa ID, etc.). Used to upsert on re-upload." />
                    </Label>
                    <Select value={bulkMarketplace} onValueChange={(v) => { setBulkMarketplace(v); setBulkPreview(null); }}>
                      <SelectTrigger data-testid="bulk-marketplace-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["Amazon","Flipkart","Nykaa","Blinkit","Zepto","Swiggy Instamart"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">CSV / XLSX file</Label>
                    <Input data-testid="bulk-file-input" type="file" accept=".csv,.xlsx,.xls" onChange={(e) => { setBulkFile(e.target.files?.[0] || null); setBulkPreview(null); }} />
                  </div>
                </div>

                {(bulkPreviewing || bulkSubmitting) && bulkFile && (
                  <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="bulk-progress">
                    <div className="flex items-center gap-2 mb-2">
                      <FileSpreadsheet className="h-3.5 w-3.5 text-slate-700" />
                      <div className="text-xs font-semibold text-slate-900 truncate flex-1">{bulkFile.name}</div>
                      <div className="text-[10px] text-slate-500">
                        {bulkServerWait
                          ? (bulkPreviewing ? "Parsing & analysing rows…" : "Importing rows…")
                          : `${bulkProgress}%`}
                      </div>
                    </div>
                    <Progress value={bulkProgress} className="h-2" />
                    <div className="mt-2 text-[11px] flex items-center gap-1.5 text-slate-600">
                      {bulkServerWait ? (
                        <><Loader2 className="h-3 w-3 animate-spin text-amber-600" />
                          <span className="text-amber-700">{bulkPreviewing ? "Server is computing the dry-run preview" : "Server is upserting products and rematching uploads"}</span></>
                      ) : (
                        <><Loader2 className="h-3 w-3 animate-spin" />
                          <span>{bulkPreviewing ? "Uploading file for preview…" : "Uploading file for import…"}</span></>
                      )}
                    </div>
                  </div>
                )}

                {bulkPreview && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-4 text-sm text-amber-900" data-testid="bulk-preview-summary">
                    <div className="font-semibold flex items-center gap-2">
                      <span className="inline-block h-2 w-2 rounded-full bg-amber-500" />
                      Dry-run preview — nothing has been written yet
                    </div>
                    <div className="mt-2 text-xs grid grid-cols-4 gap-2">
                      <div><span className="font-bold text-emerald-700">{bulkPreview.would_create}</span> would create</div>
                      <div><span className="font-bold text-blue-700">{bulkPreview.would_update}</span> would update</div>
                      <div><span className="font-bold text-rose-700">{bulkPreview.skipped}</span> skipped</div>
                      <div className="text-amber-800">{bulkPreview.total_rows} rows total</div>
                    </div>
                    {!!bulkPreview.preview?.length && (
                      <details className="mt-2 text-xs">
                        <summary className="cursor-pointer font-semibold">Show first {bulkPreview.preview.length} rows</summary>
                        <div className="mt-1 max-h-48 overflow-y-auto rounded border border-amber-200 bg-white">
                          <table className="w-full text-[11px]">
                            <thead className="bg-amber-50 text-amber-800">
                              <tr><th className="text-left px-2 py-1">Row</th><th className="text-left px-2 py-1">Action</th><th className="text-left px-2 py-1">Product</th><th className="text-left px-2 py-1">ID</th></tr>
                            </thead>
                            <tbody>
                              {bulkPreview.preview.map((p, i) => (
                                <tr key={i} className="border-t border-amber-100">
                                  <td className="px-2 py-1 font-mono">{p.row}</td>
                                  <td className="px-2 py-1">
                                    <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-bold uppercase ${
                                      p.action === "create" ? "bg-emerald-100 text-emerald-800" :
                                      p.action === "update" ? "bg-blue-100 text-blue-800" :
                                      "bg-rose-100 text-rose-800"}`}>{p.action}</span>
                                  </td>
                                  <td className="px-2 py-1">{p.product_name || "—"}</td>
                                  <td className="px-2 py-1 font-mono text-slate-600">{p.marketplace_identifier || ""}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </details>
                    )}
                    {!!bulkPreview.errors?.length && (
                      <details className="mt-2 text-xs">
                        <summary className="cursor-pointer text-rose-700 font-semibold">{bulkPreview.errors.length} row error(s)</summary>
                        <ul className="mt-1 ml-4 list-disc text-rose-800">
                          {bulkPreview.errors.slice(0, 10).map((e, i) => <li key={i}>Row {e.row}: {e.reason}</li>)}
                        </ul>
                      </details>
                    )}
                  </div>
                )}

                {bulkResult && (
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-4 text-sm text-emerald-900" data-testid="bulk-result-summary">
                    <div className="font-semibold">Import complete</div>
                    <div className="mt-1 text-xs grid grid-cols-3 gap-2">
                      <div><span className="font-semibold">{bulkResult.created}</span> created</div>
                      <div><span className="font-semibold">{bulkResult.updated}</span> updated</div>
                      <div><span className="font-semibold">{bulkResult.skipped}</span> skipped</div>
                    </div>
                    {!!bulkResult.errors?.length && (
                      <details className="mt-2 text-xs">
                        <summary className="cursor-pointer text-rose-700 font-semibold">{bulkResult.errors.length} row error(s)</summary>
                        <ul className="mt-1 ml-4 list-disc text-rose-800">
                          {bulkResult.errors.slice(0, 10).map((e, i) => <li key={i}>Row {e.row}: {e.reason}</li>)}
                        </ul>
                      </details>
                    )}
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setBulkOpen(false)}>Close</Button>
                <Button data-testid="bulk-preview-btn" onClick={previewBulk} disabled={bulkPreviewing || !bulkFile} variant="outline">
                  {bulkPreviewing ? "Previewing…" : "Preview"}
                </Button>
                <Button data-testid="bulk-submit-btn" onClick={commitBulk} disabled={bulkSubmitting || !bulkPreview} className="bg-slate-900 text-white hover:bg-slate-800">
                  {bulkSubmitting ? "Importing…" : `Import ${bulkPreview ? `(${(bulkPreview.would_create||0) + (bulkPreview.would_update||0)} rows)` : ""}`}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(EMPTY); } }}>
            <DialogTrigger asChild>
              <Button data-testid="product-add-btn" disabled={!selectedBrandId} className="bg-slate-900 text-white hover:bg-slate-800 rounded-lg">
                <Plus className="h-4 w-4 mr-1" /> New product
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? "Edit product" : "Create product"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-2">
              <F label="Product name" required><Input data-testid="product-form-name" value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })} /></F>
              <F label={<span className="inline-flex items-center gap-1">SKU <InfoTip term="SKU" /></span>}><Input value={form.sku || ""} onChange={(e) => setForm({ ...form, sku: e.target.value })} /></F>
              <F label={<span className="inline-flex items-center gap-1">ASIN <InfoTip term="ASIN" /></span>}><Input value={form.asin || ""} onChange={(e) => setForm({ ...form, asin: e.target.value })} /></F>
              <F label="Product type"><Input value={form.product_type || ""} onChange={(e) => setForm({ ...form, product_type: e.target.value })} /></F>
              <F label="Category">
                <Select value={form.category || ""} onValueChange={(v) => setForm({ ...form, category: v, subcategory: "" })}>
                  <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {categories.map((c) => <SelectItem key={c.id} value={c.category_name}>{c.category_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </F>
              <F label="Subcategory">
                <Select value={form.subcategory || ""} onValueChange={(v) => setForm({ ...form, subcategory: v })} disabled={!form.category}>
                  <SelectTrigger><SelectValue placeholder={form.category ? "Select subcategory" : "Pick a category first"} /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {subOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </F>
              <F label="Price (₹)"><Input type="number" value={form.price || ""} onChange={(e) => setForm({ ...form, price: e.target.value })} /></F>
              <F label={<span className="inline-flex items-center gap-1">MRP (₹) <InfoTip term="MRP" /></span>}><Input type="number" value={form.mrp || ""} onChange={(e) => setForm({ ...form, mrp: e.target.value })} /></F>
              <F label={<span className="inline-flex items-center gap-1">Margin (%) <InfoTip term="MARGIN_PCT" /></span>}><Input type="number" value={form.margin_percentage || ""} onChange={(e) => setForm({ ...form, margin_percentage: e.target.value })} /></F>
              <F label="Inventory status">
                <Select value={form.inventory_status} onValueChange={(v) => setForm({ ...form, inventory_status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{INV_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label={<span className="inline-flex items-center gap-1">Lifecycle <InfoTip term="LIFECYCLE" /></span>}>
                <Select value={form.lifecycle_stage} onValueChange={(v) => setForm({ ...form, lifecycle_stage: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{LIFECYCLE.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label={<span className="inline-flex items-center gap-1">Hero SKU <InfoTip term="HERO_SKU" /></span>}>
                <div className="flex items-center gap-3 h-9">
                  <Switch checked={!!form.is_hero_sku} onCheckedChange={(v) => setForm({ ...form, is_hero_sku: v })} />
                  <span className="text-sm text-slate-600">{form.is_hero_sku ? "Yes" : "No"}</span>
                </div>
              </F>
              <F label="Features" full><Textarea value={form.features || ""} onChange={(e) => setForm({ ...form, features: e.target.value })} /></F>
              <F label="Ingredients" full><Textarea value={form.ingredients || ""} onChange={(e) => setForm({ ...form, ingredients: e.target.value })} /></F>
              <F label="Benefits" full><Textarea value={form.benefits || ""} onChange={(e) => setForm({ ...form, benefits: e.target.value })} /></F>
              <F label="Target customer" full><Textarea value={form.target_customer || ""} onChange={(e) => setForm({ ...form, target_customer: e.target.value })} /></F>
              <F label="Current Amazon title" full><Textarea value={form.current_title || ""} onChange={(e) => setForm({ ...form, current_title: e.target.value })} /></F>
              <F label="Current bullets" full><Textarea value={form.current_bullets || ""} onChange={(e) => setForm({ ...form, current_bullets: e.target.value })} /></F>
              <F label={<span className="inline-flex items-center gap-1">Backend keywords <InfoTip term="BACKEND_KW" /></span>} full><Textarea value={form.current_backend_keywords || ""} onChange={(e) => setForm({ ...form, current_backend_keywords: e.target.value })} /></F>
            </div>
            <DialogFooter>
              <Button data-testid="product-form-save" onClick={save} className="bg-slate-900 text-white hover:bg-slate-800">{editing ? "Save" : "Create"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200">
        {[
          { key: "active", label: "Active", count: counts.active, testid: "tab-active" },
          { key: "inactive", label: "Inactive", count: counts.inactive, testid: "tab-inactive" },
        ].map((t) => (
          <button
            key={t.key}
            data-testid={t.testid}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 -mb-px text-sm font-semibold border-b-2 transition-colors ${
              tab === t.key
                ? "border-slate-900 text-slate-900"
                : "border-transparent text-slate-500 hover:text-slate-900"
            }`}
          >
            {t.label} <span className="ml-1 text-xs font-normal text-slate-400">({t.count})</span>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 px-1" data-testid="products-filter-bar">
        <div className="flex-1 min-w-[220px] max-w-md">
          <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Search</Label>
          <Input
            data-testid="products-search"
            placeholder="Product, SKU or ASIN…"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            className="h-9 text-sm"
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold inline-flex items-center gap-1">
            Min ₹ <InfoTip term="PRICE_FILTER" />
          </Label>
          <Input
            data-testid="products-filter-price-min"
            type="number"
            min="0"
            value={priceMin}
            onChange={(e) => setPriceMin(e.target.value)}
            placeholder="0"
            className="h-9 text-sm w-28"
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Max ₹</Label>
          <Input
            data-testid="products-filter-price-max"
            type="number"
            min="0"
            value={priceMax}
            onChange={(e) => setPriceMax(e.target.value)}
            placeholder="∞"
            className="h-9 text-sm w-28"
          />
        </div>
        {(filterText || priceMin || priceMax) && (
          <Button
            data-testid="products-filter-reset"
            variant="ghost"
            size="sm"
            onClick={() => { setFilterText(""); setPriceMin(""); setPriceMax(""); }}
            className="h-9"
          >
            Reset
          </Button>
        )}
        <div className="ml-auto text-xs text-slate-500" data-testid="products-filter-summary">
          Showing <span className="font-semibold text-slate-900">{visibleItems.length}</span> of {items.length}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="products-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">
            <tr>
              <th className="px-4 py-3 text-left">Product</th>
              <th className="px-4 py-3 text-left">SKU / ASIN</th>
              <th className="px-4 py-3 text-left">Type</th>
              <th className="px-4 py-3 text-left">Inventory</th>
              <th className="px-4 py-3 text-left">Lifecycle</th>
              <th className="px-4 py-3 text-right">Price</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {visibleItems.map((p) => (
              <tr key={p.id} className={`border-b border-slate-100 hover:bg-slate-50/50 ${tab === "inactive" ? "opacity-70" : ""}`}>
                <td className="px-4 py-3">
                  <div className="font-semibold text-slate-900 flex items-center gap-2">
                    {p.is_hero_sku ? <Star strokeWidth={2} className="h-3.5 w-3.5 text-amber-500 fill-amber-400" /> : null}
                    {p.product_name}
                  </div>
                  <div className="text-[11px] text-slate-500">{(p.benefits || "").slice(0, 80)}</div>
                </td>
                <td className="px-4 py-3 text-xs font-mono text-slate-600">{p.sku}<br />{p.asin}</td>
                <td className="px-4 py-3 text-slate-700">{p.product_type || "—"}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
                    p.inventory_status === "Healthy" ? "border-emerald-200 bg-emerald-50 text-emerald-700" :
                    p.inventory_status === "Low" ? "border-orange-200 bg-orange-50 text-orange-700" :
                    "border-rose-200 bg-rose-50 text-rose-700"}`}>{p.inventory_status}</span>
                </td>
                <td className="px-4 py-3 text-slate-700">{p.lifecycle_stage}</td>
                <td className="px-4 py-3 text-right text-slate-900 font-semibold">₹{p.price?.toLocaleString() || "—"}</td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-1">
                    <Button variant="ghost" size="sm" data-testid={`product-edit-${p.sku || p.id}`} onClick={() => onEdit(p)} title="Edit"><Pencil className="h-3.5 w-3.5" /></Button>
                    {tab === "active" ? (
                      <Button variant="ghost" size="sm" data-testid={`product-deactivate-${p.sku || p.id}`} onClick={() => setStatus(p, "inactive")} title="Deactivate (excludes from matching)" className="text-rose-600 hover:text-rose-800 hover:bg-rose-50">
                        <PowerOff className="h-3.5 w-3.5" />
                      </Button>
                    ) : (
                      <Button variant="ghost" size="sm" data-testid={`product-reactivate-${p.sku || p.id}`} onClick={() => setStatus(p, "active")} title="Reactivate" className="text-emerald-700 hover:text-emerald-900 hover:bg-emerald-50">
                        <Power className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {!visibleItems.length && (
              <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500 text-sm">
                {!selectedBrandId
                  ? "Select a brand to view products."
                  : !items.length
                    ? (tab === "active" ? "No active products yet for this brand." : "No inactive products.")
                    : "No products match the current filters. Try widening the price range or clearing the search."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function F({ label, required, full, children }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">
        {label}{required ? <span className="text-rose-500"> *</span> : null}
      </Label>
      {children}
    </div>
  );
}
